// /en/ltr/$segment — EN detail page
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { LTR_SEGMENTS, type LtrSegment } from "@/data/ltr-segments";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { NationalityGridLinks } from "@/components/NationalityGridLinks";
import { Phone, MessageCircle, ArrowRight, Sparkles, FileText, Wallet, Clock, ChevronRight, ShieldCheck, Award } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/ltr/$segment/")({
  loader: ({ params }) => {
    const s = LTR_SEGMENTS.find((x) => x.slug === params.segment);
    if (!s) throw notFound();
    return { segment: s };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const s = loaderData.segment;
    const url = `${SITE}/en/ltr/${s.slug}`;
    const title = `${s.titleEn} — Criteria, Steps & Fees 2026 | NYC`;
    const desc = s.quickAnswerEn.slice(0, 155);
    return {
      meta: [
        { title: ctaTitle(title, "/en/ltr/$segment", "en") }, { name: "description", content: ctaDesc(desc, "/en/ltr/$segment", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/ltr/$segment", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/ltr/$segment", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/ltr/${s.slug}`, `/en/ltr/${s.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: s.titleEn, serviceType: s.titleEn, description: desc,
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: String(s.servicePriceTHB), priceCurrency: "THB", description: `${s.titleEn} application service` },
            }),
          },
          howToSchema({
            name: `How to apply: ${s.titleEn}`,
            description: s.taglineEn,
            totalTime: "P30D",
            estimatedCost: { currency: "THB", value: String(s.servicePriceTHB + 50000) },
            inLanguage: "en",
            steps: s.stepsEn,
          }),
          faqPageSchema(s.faqsEn),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "LTR Visa", item: `${SITE}/en/ltr` },
                { "@type": "ListItem", position: 3, name: s.titleEn, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-3xl font-extrabold">LTR segment not found</h1>
      <Link to="/en/ltr" className="mt-6 inline-block rounded-lg bg-primary px-5 py-3 font-bold text-primary-foreground">Back to LTR hub</Link>
    </main>
  ),
  errorComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-2xl font-bold">Something went wrong</h1>
      <Link to="/en/ltr" className="mt-4 inline-block text-primary">Back to LTR hub</Link>
    </main>
  ),
  component: LtrSegmentEn,
});

function LtrSegmentEn() {
  const { segment: s } = Route.useLoaderData() as { segment: LtrSegment };
  const others = LTR_SEGMENTS.filter((x) => x.slug !== s.slug);
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-5xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/en" className="hover:text-primary">Home</Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <Link to="/en/ltr" className="hover:text-primary">LTR</Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <span>{s.titleEn}</span>
          </nav>
          <div className="text-4xl">{s.icon}</div>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">{s.titleEn}</h1>
          <p className="mt-3 max-w-3xl text-lg text-muted-foreground">{s.taglineEn}</p>

          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <p className="text-sm leading-relaxed text-foreground sm:text-base">{s.quickAnswerEn}</p>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-3 max-w-3xl">
            <div className="rounded-xl border border-border bg-card p-3">
              <Wallet className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">BOI fee</div>
              <div className="text-sm font-bold">THB 50,000</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <Clock className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">Processing</div>
              <div className="text-sm font-bold">{s.processingDays}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <Award className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">Key criteria</div>
              <div className="text-xs font-bold line-clamp-2">{s.qualifyEn}</div>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> Call +66 92 242 9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-2xl font-extrabold">Application steps</h2>
              <ol className="mt-4 space-y-3">
                {s.stepsEn.map((step, i) => (
                  <li key={i} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">{i + 1}</div>
                      <div>
                        <div className="font-bold">{step.name}</div>
                        <p className="mt-1 text-sm text-muted-foreground">{step.text}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">Required documents</h2>
              <ul className="mt-4 space-y-2">
                {s.docsEn.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 text-sm">
                    <FileText className="h-4 w-4 shrink-0 text-primary" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">Frequently asked questions</h2>
              <div className="mt-4 space-y-3">
                {s.faqsEn.map((f, i) => (
                  <details key={i} className="rounded-xl border border-border bg-card p-4">
                    <summary className="cursor-pointer font-bold">{f.q}</summary>
                    <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-bold">Who qualifies?</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.whoEn}</p>
            </div>
            <div className="rounded-2xl border border-primary/40 bg-primary/5 p-5">
              <h3 className="font-bold flex items-center gap-1.5"><Award className="h-4 w-4 text-primary" /> Key perks</h3>
              <p className="mt-2 text-sm">{s.perksEn}</p>
            </div>
            <div className="rounded-2xl border border-primary bg-primary/5 p-5">
              <h3 className="font-bold">NYC service</h3>
              <p className="mt-2 text-2xl font-extrabold text-primary">THB {s.servicePriceTHB.toLocaleString()}</p>
              <p className="mt-1 text-xs text-muted-foreground">Excludes BOI fee 50,000 THB. Includes qualification check, document prep, BOI submission, and visa stamp at OSS</p>
              <a href="tel:+66922429293" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground">
                <Phone className="h-4 w-4" /> Call for advice
              </a>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-bold">Related services</h3>
              <ul className="mt-2 space-y-1 text-sm">
                <li><Link to="/en/translate" className="text-primary hover:underline">Certified translation</Link></li>
                <li><Link to="/en/apostille" className="text-primary hover:underline">Apostille / legalisation</Link></li>
                <li><Link to="/en/visa-compliance" className="text-primary hover:underline">TM30 + 90-day report</Link></li>
                <li><Link to="/en/driving-license" className="text-primary hover:underline">Thai 5-year driving license</Link></li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xl font-extrabold"><ShieldCheck className="mr-2 inline h-5 w-5 text-primary" />Other LTR segments</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((o) => (
              <Link key={o.slug} to="/en/ltr/$segment" params={{ segment: o.slug }} className="rounded-xl border border-border bg-card p-3 hover:border-primary">
                <div className="flex items-center gap-2"><span className="text-xl">{o.icon}</span><span className="font-bold text-sm">{o.titleEn}</span></div>
                <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{o.taglineEn}</div>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">View <ArrowRight className="h-3 w-3" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <NationalityGridLinks lang="en" hrefFor={(n) => `/en/ltr/${s.slug}/for/${n}`} />
      <TrustLayer variant="compact" />
    </main>
  );
}
